import moment from "moment";
import { cacheService } from "../Api/services";
import { BaseMetadataType } from "../Api/Types/base-metadata-type";
import { DbAssetObjectInfos } from "../Api/Types/db-asset-object-infos";
import { DbMetadataFieldInfos } from "../Api/Types/db-metadata-field-infos";
import { IDbBaseMetadataValueInfos } from "../Api/Types/i-db-base-metadata-value-infos";
import { ObjectLayer } from "../Api/Types/object-layer";
import { PermissionType } from "../Api/Types/permission-type";
import { ComputeMetadataBaseType } from "./functions";
import { UserHasAcl, UserHasLicence } from "../../Utils/helpers";
import { AclTypes } from "../Api/Types/acl-types";
import { ObjectType } from "../Api/Types/object-type";
import { Licences } from "../Api/Types/licences";
import { ObjectTag } from "../Api/Types/object-tag";
import { ObjectStatus } from "../Api/Types/object-status";
import { ObjectClass } from "../Api/Types/object-class";
import { PlatformMode } from "../Api/Types/platform-mode";

const virtualMetadata = [
  "allAudioLanguages",
  "allAudioVersions",
  "allVideoQualities",
  "allSubtitleLanguages",
  "programRestored",
  "duration",
  "refVideoAssetIds",
];

export function GetMetadataByTagName(
  assetObject: DbAssetObjectInfos | null | undefined,
  tagName: string,
  metadatas?: DbMetadataFieldInfos[]
): IDbBaseMetadataValueInfos | null {
  if (!assetObject) return null;
  if (virtualMetadata.includes(tagName)) return assetObject.metadata?.find((m) => m.fieldName === tagName) || null;

  const metadataFromStorage: DbMetadataFieldInfos[] = metadatas !== undefined ? metadatas : cacheService.GetMetadata();
  const metadataByTagName = metadataFromStorage.find((m) => m.tagName === tagName && m.layer === assetObject.layer);

  if (metadataByTagName === undefined || metadataByTagName.id === null) return null;

  return GetMetadataById(assetObject, metadataByTagName.id);
}

export function GetMetadataByTagNames(
  assetObject: DbAssetObjectInfos | null,
  tagNames: string[],
  metadatas?: DbMetadataFieldInfos[]
): IDbBaseMetadataValueInfos[] | null {
  let list: IDbBaseMetadataValueInfos[] = [];

  tagNames.forEach((tagName) => {
    let res = GetMetadataByTagName(assetObject, tagName, metadatas);
    if (res !== null) list.push(res);
  });

  if (list.length === 0) return null;

  return list;
}

export function GetMetadataById(
  assetObject: DbAssetObjectInfos | null,
  metadataId: number
): IDbBaseMetadataValueInfos | null {
  if (assetObject === null) return null;
  if (assetObject.metadata === null) return null;
  const metadata = assetObject.metadata.find((m) => m.id === metadataId);
  return metadata || null;
}

export function GetChildObjectById(assetObject: DbAssetObjectInfos | null, childId: number): DbAssetObjectInfos | null {
  if (assetObject === null) return null;
  if (assetObject.childObjects === null) return null;
  const childObject = assetObject.childObjects.find((a) => a.id === childId);
  return childObject || null;
}

export function GetChildRecursivelyObjectById(
  assetObject: DbAssetObjectInfos | null | undefined,
  childId: number
): DbAssetObjectInfos | null {
  if (!assetObject || !assetObject.childObjects) return null;

  const match = assetObject.childObjects.find((a) => a.id === childId);

  if (match !== undefined) return match;

  for (let i = 0; i < assetObject.childObjects.length; ++i)
    if (assetObject.childObjects[i] !== null) {
      let ret = GetChildRecursivelyObjectById(assetObject.childObjects[i], childId);
      if (ret !== null) return ret;
    }

  return null;
}

export function GetChildsRecursivelyByIds(
  assetObject: DbAssetObjectInfos | null,
  childsIds: number[]
): DbAssetObjectInfos[] {
  let output: DbAssetObjectInfos[] = [];
  for (let i = 0; i < childsIds.length; i++) {
    const child = GetChildRecursivelyObjectById(assetObject, childsIds[i]);
    if (child) output.push(child);
  }

  return output;
}

export function CountChildRecursivelyObjectById(assetObject: DbAssetObjectInfos | null): number {
  let counter = 0;
  if (assetObject === null) return 0;
  if (assetObject.childObjects === null) return 0;
  counter += assetObject.childObjects.filter((a) => a.layer === ObjectLayer.Asset).length;

  for (let i = 0; i < assetObject.childObjects.length; ++i)
    if (assetObject.childObjects[i] !== null) {
      counter += CountChildRecursivelyObjectById(assetObject.childObjects[i]);
    }

  return counter;
}

export function FlattenObjectListFromGraph(
  assetObject: DbAssetObjectInfos | null,
  objectLayers: ObjectLayer[],
  skipChildrenFromTypes?: ObjectType[],
  depth?: number
): DbAssetObjectInfos[] | null {
  let result: DbAssetObjectInfos[] = [];
  if (depth === 0) return null;
  if (assetObject === null) return null;
  if (assetObject.childObjects === null) return null;

  for (let i = 0; i < assetObject.childObjects.length; ++i) {
    if (assetObject.childObjects[i].type === ObjectType.UploadContainer) continue;

    if (skipChildrenFromTypes?.includes(assetObject.type)) continue;

    if (objectLayers.indexOf(assetObject.childObjects[i].layer!) >= 0) result.push(assetObject.childObjects[i]);

    if (assetObject.childObjects[i].childObjects !== null) {
      let child: DbAssetObjectInfos[] | null = FlattenObjectListFromGraph(
        assetObject.childObjects[i],
        objectLayers,
        skipChildrenFromTypes,
        depth !== undefined ? depth - 1 : undefined
      );

      if (child !== null) for (let r = 0; r < child?.length; ++r) result.push(child[r]);
    }
  }

  return result;
}

export function CreateNewDbMetadataValue(
  id: number,
  type: BaseMetadataType,
  fieldName: string,
  value: any
): IDbBaseMetadataValueInfos {
  return {
    //$type: ComputeValueBaseTypeFromMetaBaseType(type),
    $type: ComputeMetadataBaseType(type),
    creationTime: moment().toISOString(),
    fieldName: fieldName,
    id: id,
    value: value,
    fieldLabel: null,
    fieldType: type,
    targetLayer: null,
    updateTime: null,
    valueLabel: null,
  };
}

export function AddNewMetadataToAsset(
  assetObject: DbAssetObjectInfos,
  meta: { fieldName: string; value: any; id: number; type: BaseMetadataType }
) {
  if (assetObject.metadata?.find((m) => m.id === meta.id)) return;

  const newMeta = CreateNewDbMetadataValue(meta.id, meta.type, meta.fieldName, meta.value);
  assetObject.metadata?.push(newMeta);
}

export function UpdateMetadataOfAsset(assetObject: DbAssetObjectInfos, meta: { id: number; value: any }) {
  if (!assetObject.metadata?.find((m) => m.id === meta.id)) return;
  const metaIndex = assetObject.metadata?.findIndex((m) => m.id === meta.id);
  assetObject.metadata[metaIndex].value = meta.value;
}

export function AddOrUpdateMetadataToAsset(
  assetObject: DbAssetObjectInfos,
  meta: { fieldName: string; value: any; id: number; type: BaseMetadataType }
) {
  if (assetObject.metadata?.find((m) => m.id === meta.id)) AddNewMetadataToAsset(assetObject, meta);
  else UpdateMetadataOfAsset(assetObject, meta);
}

export function GetAssetContentId(title: DbAssetObjectInfos, asset: DbAssetObjectInfos) {
  let titleContentIds = title.childObjects?.map((x) => x.id);
  let assetParentIds = asset.allParentObjectIds;
  let contentId = assetParentIds?.find((x) => titleContentIds?.includes(x));

  return title.childObjects?.find((x) => x.id === contentId);
}

export function CountAssetsInContent(title: DbAssetObjectInfos) {
  let assetCount = 0;

  if (title.childObjects) {
    for (const childObject of title.childObjects) {
      if (childObject.layer === "Asset") {
        assetCount++;
      } else if (childObject.layer === "Group") {
        assetCount += CountAssetsInContent(childObject);
      }
    }
  }
  return assetCount;
}

export function IsPermissionGranted(asset: DbAssetObjectInfos | null | undefined, permission: PermissionType) {
  if (!asset || !IsDriveObject(asset)) return true;
  if (!asset.permsInherit) return false;

  switch (permission) {
    case PermissionType.Share: // UserHasAcl(AclTypes.aclShareCreate)
      return IsObjectShareable(asset) && asset.permsInherit[permission];
    case PermissionType.Trash: // UserHasAcl(AclTypes.aclAssetDelete)
      return UserHasAcl(AclTypes.aclObjectTrashPut) && asset.permsInherit[permission];
    case PermissionType.Access:
    case PermissionType.Comment:
    case PermissionType.CreateObject:
    case PermissionType.Download:
    case PermissionType.MetadataEdit:
      return asset.permsInherit[permission];
  }
}

export function IsAssetDownloadable(asset: DbAssetObjectInfos | null | undefined) {
  if (!asset || !asset.permsInherit) return false;

  return (
    [
      ObjectType.Video,
      ObjectType.Audio,
      ObjectType.Image,
      ObjectType.Document,
      ObjectType.ImageSequence,
      ObjectType.Folder,
      ObjectType.Subtitle,
    ].includes(asset.type) && IsPermissionGranted(asset, PermissionType.Download)
  );
}

export function IsClipDownloadable(clip: DbAssetObjectInfos | null | undefined) {
  if (!clip || !clip.permsInherit) return false;

  return [ObjectLayer.Clip].includes(clip.layer) && IsPermissionGranted(clip, PermissionType.Download);
}

export function IsAssetOrderable(asset: DbAssetObjectInfos | null | undefined) {
  if (!asset || !asset.permsInherit) return false;

  return (
    UserHasAcl(AclTypes.aclSectionOrderAccess) &&
    asset.type !== ObjectType.Folder &&
    IsPermissionGranted(asset, PermissionType.CreateObject)
  );
}

export const TitleShouldBeWatermarked = (title: DbAssetObjectInfos): boolean => {
  const watermarkForceDelayDays = cacheService.GetAuthLoginResponse()?.watermarkForceDelayDays;
  if (!watermarkForceDelayDays || watermarkForceDelayDays === 0) return false;

  const productionDate = title?.metadata?.find(
    (x) => x.id === cacheService.GetMetadata().find((m) => m.tagName === "productionDate")?.id
  )?.value;

  return (
    productionDate &&
    (moment(productionDate).isSameOrAfter(moment().add(-watermarkForceDelayDays, "d")) ||
      title.tags?.includes(ObjectTag.Watermark)) &&
    UserHasLicence(Licences.WatermarkForceOutput)
  );
};

function IsObjectTypeShareable(objectType: ObjectType): boolean {
  return (
    objectType === ObjectType.Video ||
    objectType === ObjectType.Audio ||
    objectType === ObjectType.Image ||
    objectType === ObjectType.Document ||
    objectType === ObjectType.ImageSequence
  );
}

export function IsContentShareable(object: DbAssetObjectInfos): boolean {
  let contentSubTypeMetaId = cacheService.GetMetadata().find((x) => x.tagName === "contentSubType")?.id;
  return (
    object.childObjects?.some(
      (x) =>
        x.type === ObjectType.Video &&
        x.classification === ObjectClass.Mezz &&
        x.status === ObjectStatus.Accepted &&
        !x.isDeleted &&
        (x.metadata?.find((m) => m.id === contentSubTypeMetaId)?.value === "Program" ||
          x.metadata?.find((m) => m.id === contentSubTypeMetaId)?.value === "Program Restored")
    ) ?? false
  );
}

function IsObjectStatusShareable(objectStatus: ObjectStatus): boolean {
  const platformMode = cacheService.GetPlatformMode();
  return (
    objectStatus === ObjectStatus.Accepted ||
    objectStatus === ObjectStatus.Rejected ||
    objectStatus === ObjectStatus.RejectedAuto ||
    objectStatus === ObjectStatus.ToBeQCed ||
    objectStatus === ObjectStatus.QualityCheckInProgress ||
    (platformMode === PlatformMode.Adam &&
      (objectStatus === ObjectStatus.NeedCheckOrEditing ||
        objectStatus === ObjectStatus.AcceptedForBroadcast ||
        objectStatus === ObjectStatus.Broadcasted ||
        objectStatus === ObjectStatus.Delivered))
  );
}

function IsObjectClassShareable(objectClass: ObjectClass | null): boolean {
  return (
    objectClass === ObjectClass.Mezz ||
    objectClass === ObjectClass.MezzAuto ||
    objectClass === ObjectClass.Raw ||
    objectClass === ObjectClass.Drive
  );
}

export function IsObjectShareable(object: DbAssetObjectInfos | undefined): boolean {
  if (object === undefined) return false;
  switch (object.layer) {
    case ObjectLayer.Asset:
      return (
        IsObjectTypeShareable(object.type) &&
        IsObjectStatusShareable(object.status) &&
        IsObjectClassShareable(object.classification)
      );
    case ObjectLayer.Content:
      return IsContentShareable(object);
    case ObjectLayer.Group:
      return IsDriveObject(object);
    default:
      return false;
  }
}

export function AreAllObjectsShareable(objects: DbAssetObjectInfos[]): boolean {
  return !objects.some((x) => !IsObjectShareable(x));
}

export function IsDriveObject(object: DbAssetObjectInfos): boolean {
  return object.classification === ObjectClass.Drive;
}
